import { createReducer } from '@reduxjs/toolkit'
import { Project, ProjectLevel, ProjectType } from 'interfaces/api/projects.interfaces'
import { uniqBy } from 'lodash'
import { ValidationErrorsApi } from 'packages/api'
import { createBuilder } from 'packages/redux-toolkit'

import {
  createProject,
  getAllProjects,
  getLevels,
  getProject,
  getProjectTypes,
  removeProject,
  setProjectValidationErrors,
  setSingleProject,
  updateProject,
} from './actions'

interface ProjectsState {
  list: {
    loading: boolean
    items: Project[]
    count: number
  }
  single: {
    loading: boolean
    item: Project | null
  }
  validation: ValidationErrorsApi | null
  types: ProjectType[]
  levels: ProjectLevel[]
}

const initialState: ProjectsState = {
  list: {
    loading: false,
    items: [],
    count: 0,
  },
  single: {
    loading: false,
    item: null,
  },
  validation: null,
  types: [],
  levels: [],
}

export const projectsReducer = createReducer(initialState, (builder) => {
  createBuilder(builder)
    .addCase(getAllProjects.pending, (state) => {
      state.list.loading = true
    })
    .addCase(getAllProjects.rejected, (state) => {
      state.list.loading = false
    })
    .addCase(getAllProjects.fulfilled, (state, { payload, meta }) => {
      state.list.loading = false
      if (meta.arg?.title) {
        state.list.items = uniqBy([...state.list.items, ...payload.results], 'id')
      } else {
        state.list.count = payload.count
        state.list.items = payload.results
      }
    })

    .addCase(getProject.pending, (state) => {
      state.single.loading = true
    })
    .addCase(getProject.rejected, (state) => {
      state.single.loading = false
      state.single.item = null
    })
    .addCase([getProject.fulfilled, setSingleProject], (state, { payload }) => {
      state.single.loading = false
      state.single.item = payload
    })

    .addCase(setProjectValidationErrors, (state, { payload }) => {
      state.validation = payload
    })

    .addCase([createProject.pending, updateProject.pending], (state) => {
      state.single.loading = true
    })
    .addCase([createProject.rejected, updateProject.rejected], (state) => {
      state.single.loading = false
    })
    .addCase([createProject.fulfilled, updateProject.fulfilled], (state) => {
      state.single.loading = false
    })

    .addCase(getProjectTypes.fulfilled, (state, { payload }) => {
      state.types = payload
    })

    .addCase(getLevels.fulfilled, (state, { payload }) => {
      state.levels = payload
    })

    .addCase(removeProject.fulfilled, (state, { meta }) => {
      const id = meta.arg.id
      const removed = state.list.items.some((item) => item.id === id)
      state.list.items = state.list.items.filter((item) => item.id !== id)
      if (removed) {
        state.list.count = Math.max(0, state.list.count - 1)
      }
    })
})
